import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, RefreshCw, Check, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import PlanFormDialog from "../components/PlanFormDialog";
import DeletePlanDialog from "../components/DeletePlanDialog";

const API_BASE = "https://apisellhub.com/api";

const PricingPlans = () => {
    const navigate = useNavigate();
    const { getToken } = useAuth();

    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);

    // Dialogs
    const [formOpen, setFormOpen] = useState(false);
    const [editingPlan, setEditingPlan] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deletingPlan, setDeletingPlan] = useState(null);

    const fetchPlans = async () => {
        try {
            setLoading(true);
            const token = getToken();
            if (!token) {
                setLoading(false);
                return;
            }

            const response = await axios.get(`${API_BASE}/super-admin/pricing-plans`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            setPlans(response.data?.data ?? response.data ?? []);
        } catch (err) {
            console.error("Pricing plans API error:", err);
            if (err.response?.status === 401) {
                navigate("/center/login", { replace: true });
                sessionStorage.removeItem("Market-token");
                toast.error("Token Expired. Please login again.");
            } else {
                toast.error("Failed to load pricing plans");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPlans();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCreate = () => {
        setEditingPlan(null);
        setFormOpen(true);
    };

    const handleEdit = (plan) => {
        setEditingPlan(plan);
        setFormOpen(true);
    };

    const handleDelete = (plan) => {
        setDeletingPlan(plan);
        setDeleteOpen(true);
    };

    const handleFormSuccess = () => {
        setFormOpen(false);
        setEditingPlan(null);
        fetchPlans();
    };

    const handleDeleteSuccess = () => {
        setDeleteOpen(false);
        setDeletingPlan(null);
        fetchPlans();
    };

    const formatGateways = (gateways) => {
        if (!gateways) return "—";
        if (typeof gateways === "string") {
            try {
                gateways = JSON.parse(gateways);
            } catch {
                return gateways;
            }
        }
        if (!Array.isArray(gateways) || gateways.length === 0) return "—";
        return gateways.join(", ");
    };

    return (
        <div className="p-4 lg:p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Pricing Plans</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Create, edit, and manage seller subscription plans.
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={fetchPlans}
                        disabled={loading}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </button>
                    <button
                        onClick={handleCreate}
                        className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        New Plan
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                            <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                                <th className="px-4 py-3 font-semibold">Name</th>
                                <th className="px-4 py-3 font-semibold">Price</th>
                                <th className="px-4 py-3 font-semibold">Duration</th>
                                <th className="px-4 py-3 font-semibold">Products</th>
                                <th className="px-4 py-3 font-semibold">Stores</th>
                                <th className="px-4 py-3 font-semibold">Team</th>
                                <th className="px-4 py-3 font-semibold">Gateways</th>
                                <th className="px-4 py-3 font-semibold">Custom Domain</th>
                                <th className="px-4 py-3 font-semibold">Status</th>
                                <th className="px-4 py-3 font-semibold text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={10} className="px-4 py-10 text-center text-gray-600">
                                        Loading plans…
                                    </td>
                                </tr>
                            ) : plans.length === 0 ? (
                                <tr>
                                    <td colSpan={10} className="px-4 py-10 text-center text-gray-500">
                                        No pricing plans yet. Click "New Plan" to create one.
                                    </td>
                                </tr>
                            ) : (
                                plans.map((plan) => (
                                    <tr key={plan.id} className="border-t border-gray-200 hover:bg-gray-50 text-gray-900">
                                        <td className="px-4 py-3">
                                            <div className="font-semibold text-gray-900">{plan.name}</div>
                                            {plan.description && (
                                                <div className="text-xs text-gray-500 mt-0.5 line-clamp-1 max-w-xs">
                                                    {plan.description}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 font-semibold text-gray-900">${Number(plan.price).toFixed(2)}</td>
                                        <td className="px-4 py-3 text-gray-900">{plan.plan_days} days</td>
                                        <td className="px-4 py-3 text-gray-900">{plan.max_products}</td>
                                        <td className="px-4 py-3 text-gray-900">{plan.max_stores}</td>
                                        <td className="px-4 py-3 text-gray-900">{plan.max_team_members}</td>
                                        <td className="px-4 py-3 text-xs text-gray-700">{formatGateways(plan.payment_gateways)}</td>
                                        <td className="px-4 py-3">
                                            {plan.allow_custom_domain ? (
                                                <Check className="h-4 w-4 text-green-600" />
                                            ) : (
                                                <X className="h-4 w-4 text-gray-400" />
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            {plan.status ? (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                    Active
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                                    Inactive
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex justify-end gap-1">
                                                <button
                                                    onClick={() => handleEdit(plan)}
                                                    className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900"
                                                    title="Edit"
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(plan)}
                                                    className="p-2 rounded-lg hover:bg-red-50 text-red-600 hover:text-red-700"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Form Dialog */}
            <PlanFormDialog
                open={formOpen}
                plan={editingPlan}
                onClose={() => {
                    setFormOpen(false);
                    setEditingPlan(null);
                }}
                onSuccess={handleFormSuccess}
            />

            {/* Delete Dialog */}
            <DeletePlanDialog
                open={deleteOpen}
                plan={deletingPlan}
                onClose={() => {
                    setDeleteOpen(false);
                    setDeletingPlan(null);
                }}
                onSuccess={handleDeleteSuccess}
            />
        </div>
    );
};

export default PricingPlans;
